/**
 * Composable для управления темой приложения.
 *
 * Единый владелец состояния темы — settings store (Theme.LIGHT/DARK/AUTO,
 * персист в localStorage 'crm-user-settings'). Переключение применяется
 * через атрибут [data-theme] на <html>; CSS-переменные тем определены в
 * design-system-variables.css. Этот composable — тонкая обёртка над стором,
 * сохраняющая прежний публичный API (currentTheme/isDark/toggleTheme/setTheme).
 */
import { computed } from 'vue'
import { useSettingsStore } from '@/features/settings/stores/settings.store'
import { Theme } from '@/features/settings/types/settings.types'

type ThemeName = 'light' | 'dark'

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches

export const useTheme = () => {
  const settingsStore = useSettingsStore()

  /** Эффективная (разрешённая) тема с учётом AUTO */
  const isDark = computed<boolean>(() => {
    const theme = settingsStore.settings.display.theme
    if (theme === Theme.AUTO) return prefersDark()
    return theme === Theme.DARK
  })

  const currentTheme = computed<ThemeName>(() => (isDark.value ? 'dark' : 'light'))

  /** Переключить между светлой и тёмной (выбор явный, выходим из AUTO) */
  const toggleTheme = () => {
    settingsStore.changeTheme(isDark.value ? Theme.LIGHT : Theme.DARK)
  }

  /** Установить конкретную тему */
  const setTheme = (theme: ThemeName) => {
    settingsStore.changeTheme(theme === 'dark' ? Theme.DARK : Theme.LIGHT)
  }

  return {
    currentTheme,
    isDark,
    toggleTheme,
    setTheme,
  }
}
